/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { ACTION } from '@atlaskit/editor-common/analytics';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ProviderHandler } from '@atlaskit/editor-common/provider-factory';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { token } from '@atlaskit/tokens';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import {
	isPartialCopy,
	isSelectionWithinElement,
} from '@atlassian/editor-ai-common/utils/renderer';

import { useAIExperienceCommonDataContext } from '../../hooks/useAIExperienceCommonData';
import { convertExperienceName } from '../../utils/analytics/analyticsFlow';
import { useAnalyticsFlow } from '../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../utils/analytics/utils';

// override renderer's syntax highlighting styles so it appears consistent with the editor
const rendererOverrides = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.code-block .token.token': {
		fontStyle: 'normal',
		fontWeight: token('font.weight.regular'),
		color: token('color.text'),
	},
});

const widthOverrides = css({
	// ensure that the breakout mark has the same width as the container
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-breakout-mark': {
		// breakout defines a inline width style, so use important to override it
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		width: '100% !important',
	},

	// make sure resized tables has the same width as the container
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.pm-table-container': {
		// Table defines a inline width style, so use important to override it
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		width: '100% !important',
		// Table defines a inline left style, so use important to override it
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		left: '0 !important',
	},
});

type PreviewRendererProps = {
	jsonDocument: DocNode;
	providerFactory: ProviderFactory;
	schema?: Schema;
	showTelepointer: boolean;
};

const mediaOptions = {
	allowLinking: true,
	allowCaptions: true,
};

export const PreviewRenderer = ({
	jsonDocument,
	showTelepointer,
	providerFactory,
	schema,
}: PreviewRendererProps): JSX.Element => {
	const analyticsFlow = useAnalyticsFlow();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const aiExperienceCommonData = useAIExperienceCommonDataContext();

	const experienceType = convertExperienceName(aiExperienceCommonData.configItem.key.toString());
	const lastTriggeredFrom = aiExperienceCommonData.lastTriggeredFrom;

	const previewRendererRef = useRef<HTMLDivElement>(null);
	const copiedOnceRef = useRef(false);

	// IMPORTANT: the renderer will call providerFactory.destroy() when it unmounts. This is why we need to duplicate
	// the providerFactory and add a heap of subscribers to it. This is to avoid this duplicate provider gettting out of
	// sync with the original providerFactory.
	const dupedProviderFactory = useRef(new ProviderFactory());
	useEffect(() => {
		const mentionHandler: ProviderHandler<'mentionProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		const emojiHandler: ProviderHandler<'emojiProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		const mediaHandler: ProviderHandler<'mediaProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		const extensionHandler: ProviderHandler<'extensionProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		providerFactory.subscribe('mentionProvider', mentionHandler);
		providerFactory.subscribe('emojiProvider', emojiHandler);
		providerFactory.subscribe('mediaProvider', mediaHandler);
		providerFactory.subscribe('extensionProvider', extensionHandler);

		return () => {
			providerFactory.unsubscribe('mentionProvider', mentionHandler);
			providerFactory.unsubscribe('emojiProvider', emojiHandler);
			providerFactory.unsubscribe('mediaProvider', mediaHandler);
			providerFactory.unsubscribe('extensionProvider', extensionHandler);
		};
	}, [providerFactory]);

	// Ignored via go/ees005
	// eslint-disable-next-line require-await
	const onCopyHandler = useCallback(async () => {
		const previewRendererElement = previewRendererRef.current;
		if (!analyticsFlow || !previewRendererElement) {
			return;
		}

		if (!isSelectionWithinElement(previewRendererElement)) {
			return;
		}

		if (isPartialCopy(previewRendererRef.current)) {
			analyticsFlow.addAttributes({
				contentCopiedPartially: true,
			});
		}

		// Fire ACTION.ACTIONED event for copy action
		if (fireAIAnalyticsEvent && experienceType) {
			fireAIAnalyticsEvent({
				payload: createUnifiedAnalyticsPayload({
					action: ACTION.ACTIONED,
					interactionID: analyticsFlow.getLastAiInteractionId(),
					experienceName: experienceType,
					isProactive: false,
					attributes: {
						...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
						aiResultAction: 'copied',
					},
					invokedFrom: lastTriggeredFrom,
				}),
			});
		}

		// exit if copied already for this preview
		if (copiedOnceRef.current) {
			return;
		}

		analyticsFlow.addAttributes({
			contentCopied: true,
		});
		copiedOnceRef.current = true;
	}, [analyticsFlow, fireAIAnalyticsEvent, experienceType, lastTriggeredFrom]);

	useEffect(() => {
		const unbind = bind(document, {
			type: 'copy',
			listener: onCopyHandler,
		});
		return unbind;
	}, [onCopyHandler, analyticsFlow]);

	return (
		<div
			data-testid="preview-renderer"
			css={[rendererOverrides, widthOverrides]}
			ref={previewRendererRef}
		>
			<RendererActionsContext>
				<ReactRenderer
					// EDITOR-1178
					// specify the appearance will make the renderer have a max-width of 760px when appearance is full-page
					// but AI modal could be triggered from a resized larger table, which will make the AI modal much wider than 760px
					// we want the renderer be the same wide as the AI modal, so we don't want to limit the renderer width via appearance
					// keep the appearance undefined here for clarity (it's added in EDITOR-1115)
					appearance={undefined}
					document={jsonDocument}
					media={mediaOptions}
					// By default -- the renderer uses a "default schema" which supports all
					// the nodes and marks the markdown transformer can create.
					// see packages/editor/adf-schema/src/schema/default-schema.ts for details.
					addTelepointer={showTelepointer}
					allowCustomPanels
					allowAnnotations
					allowWrapCodeBlock
					schema={schema}
					dataProviders={dupedProviderFactory.current}
					allowPlaceholderText
					UNSTABLE_allowTableAlignment
					UNSTABLE_allowTableResizing
				/>
			</RendererActionsContext>
		</div>
	);
};
